import { useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  supabase,
  dbError,
  getSignedUrlRefreshDelay,
  addSignedUrlsToPhotos,
  LIST_PHOTO_VARIANTS,
  isTransientNetworkError,
  readMemberPhotoCache,
  writeMemberPhotoCache,
  invalidateMemberPhoto,
  getMemberPhotoCacheGeneration,
  MEMBER_PHOTO_CACHE_TTL_MS,
} from '@/lib';
import { uploadError } from '@/lib/logger';
import { MemberPhoto, MemberWebcamPhoto } from '@/types';
import { mapSupabaseError } from '@/lib';
import { logActivity } from '@/utils';
import { MEMBER_PHOTO_QUERY_KEYS } from '@/constants';

const PHOTO_BUCKET = 'member-webcam-photos';
const ALL_PHOTOS_HARD_LIMIT = 100;

/**
 * Structural UUID check — deliberately lenient about version/variant bits,
 * matching what the database will accept for a `uuid[]` parameter.
 */
const MEMBER_ID_PATTERN = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Drop non-UUIDs, dedupe, sort.
 *
 * This runs BEFORE the id list becomes a query key, which is the point: two
 * callers holding the same members in a different order (or with a duplicate,
 * or with a malformed id appended) now share one cache entry instead of
 * forking a cold one apiece.
 */
function normalizeMemberIds(memberIds: readonly string[]): string[] {
  const unique = new Set<string>();

  for (const memberId of memberIds) {
    if (typeof memberId === 'string' && MEMBER_ID_PATTERN.test(memberId)) {
      unique.add(memberId);
    }
  }

  return Array.from(unique).sort();
}

type RawMemberWebcamPhoto = Pick<MemberWebcamPhoto, 'id' | 'original_photo_url' | 'thumbnail_photo_url' | 'large_photo_url' | 'uploaded_at'>;

interface PhotoWithExpiry {
  original_photo_url_expires_at?: number | null;
  large_photo_url_expires_at?: number | null;
  thumbnail_photo_url_expires_at?: number | null;
}

function findEarliestExpiry(photos: Iterable<PhotoWithExpiry>): number | null {
  let min: number | null = null;
  for (const photo of photos) {
    const expiries = [
      photo.original_photo_url_expires_at,
      photo.large_photo_url_expires_at,
      photo.thumbnail_photo_url_expires_at,
    ].filter((value): value is number => typeof value === 'number' && !Number.isNaN(value));

    if (!expiries.length) continue;
    const photoMin = Math.min(...expiries);
    if (min === null || photoMin < min) {
      min = photoMin;
    }
  }
  return min;
}

function useSignedUrlRefresh(
  photos: PhotoWithExpiry[] | null | undefined,
  refetch: () => void,
) {
  useEffect(() => {
    if (!photos || photos.length === 0) return;

    const earliestExpiry = findEarliestExpiry(photos);
    const delay = getSignedUrlRefreshDelay(earliestExpiry);
    if (delay === null) return;

    const timer = setTimeout(refetch, delay);
    return () => clearTimeout(timer);
  }, [photos, refetch]);
}

export function useMemberWebcamPhotos(memberId: string) {
  const query = useQuery({
    queryKey: MEMBER_PHOTO_QUERY_KEYS.WEBCAM_BY_MEMBER(memberId),
    queryFn: async () => {
      try {
        if (!memberId) {
          // Return empty array for missing member ID - don't throw error
          return [];
        }

        // Fetch all photo URLs
        const { data, error } = await supabase
          .from('member_webcam_photos')
          .select('id, original_photo_url, thumbnail_photo_url, large_photo_url, uploaded_at')
          .eq('member_id', memberId)
          .order('uploaded_at', { ascending: false })
          .limit(1); // Only get the latest photo

        if (error) {
          throw mapSupabaseError(error, 'fetching webcam photos', { memberId });
        }

        const photosWithSignedUrls = await addSignedUrlsToPhotos(
          (data || []) as RawMemberWebcamPhoto[],
          PHOTO_BUCKET
        );

        return photosWithSignedUrls;
      } catch (err) {
        dbError('Unexpected error in useMemberWebcamPhotos', err, { memberId });
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep data in cache for 30 minutes (renamed from cacheTime)
    // Force refetch on every mount — tab clicks on the member-profile
    // screen remount this hook, and admins expect fresh data each visit.
    refetchOnMount: 'always',
  });

  useSignedUrlRefresh(query.data, query.refetch);

  return query;
}

export function useAllMemberWebcamPhotos(memberId: string) {
  const query = useQuery({
    queryKey: MEMBER_PHOTO_QUERY_KEYS.ALL_WEBCAM_BY_MEMBER(memberId),
    queryFn: async () => {
      try {
        if (!memberId) {
          // Return empty array for missing member ID - don't throw error
          return [];
        }

        const { data, error } = await supabase
          .from('member_webcam_photos')
          .select('id, original_photo_url, thumbnail_photo_url, large_photo_url, uploaded_at')
          .eq('member_id', memberId)
          .order('uploaded_at', { ascending: false })
          .limit(ALL_PHOTOS_HARD_LIMIT);

        if (error) {
          throw mapSupabaseError(error, 'fetching all webcam photos', { memberId });
        }

        const photosWithSignedUrls = await addSignedUrlsToPhotos(
          (data || []) as RawMemberWebcamPhoto[],
          PHOTO_BUCKET
        );

        return photosWithSignedUrls;
      } catch (err) {
        dbError('Unexpected error in useAllMemberWebcamPhotos', err, { memberId });
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep data in cache for 30 minutes (renamed from cacheTime)
  });

  useSignedUrlRefresh(query.data, query.refetch);

  return query;
}

export function useCreateMemberWebcamPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      member_id: string;
      original_photo_url: string;
      thumbnail_photo_url: string;
      large_photo_url: string;
    }) => {
      try {
        // First, check if member already has photos to determine if this is an add or update
        const { data: existingPhotos, error: fetchError } = await supabase
          .from('member_webcam_photos')
          .select('id')
          .eq('member_id', data.member_id)
          .limit(1);

        if (fetchError) {
          uploadError('Error checking existing photos', fetchError, { memberId: data.member_id });
          throw new Error('Failed to check existing photos');
        }

        const isUpdate = existingPhotos && existingPhotos.length > 0;

        // Get member details for logging
        const { data: memberData, error: memberError } = await supabase
          .from('members')
          .select('first_name, last_name, email')
          .eq('id', data.member_id)
          .single();

        if (memberError) {
          uploadError('Error fetching member details for logging', memberError, { memberId: data.member_id });
          // Don't throw error, just continue without logging
        }

        // Insert the new photo
        const normalizePath = (path: string) => path.trim().replace(/^\/+/, '');

        const payload = {
          member_id: data.member_id,
          original_photo_url: normalizePath(data.original_photo_url),
          thumbnail_photo_url: normalizePath(data.thumbnail_photo_url),
          large_photo_url: normalizePath(data.large_photo_url),
        };

        const { data: insertedPhoto, error } = await supabase
          .from('member_webcam_photos')
          .insert([payload])
          .select()
          .single();

        if (error) {
          uploadError('Error creating webcam photo', error, { memberId: data.member_id });
          throw new Error('Failed to save photo');
        }

        // Log the activity
        try {
          const action = isUpdate ? 'member_webcam_photo_updated' : 'member_webcam_photo_added';
          const metadata = {
            member_name: memberData ? `${memberData.first_name} ${memberData.last_name}` : 'Unknown Member',
            member_email: memberData?.email || 'Unknown Email',
            photo_id: insertedPhoto.id,
            photo_urls: {
              original: payload.original_photo_url,
              thumbnail: payload.thumbnail_photo_url,
              large: payload.large_photo_url
            }
          };

          await logActivity(
            action,
            'member_webcam_photo',
            insertedPhoto.id,
            undefined, // No changes object needed for photo creation
            metadata,
            data.member_id
          );
        } catch (logError) {
          uploadError('Error logging webcam photo activity', logError, { memberId: data.member_id });
          // Don't throw error, photo was saved successfully
        }

        return insertedPhoto;
      } catch (err) {
        uploadError('Unexpected error in useCreateMemberWebcamPhoto', err, { memberId: data.member_id });
        throw new Error(
          err instanceof Error 
            ? err.message 
            : 'Failed to save photo. Please try again.'
        );
      }
    },
    onSuccess: (_, variables) => {
      // Drop the shared per-member entry FIRST: the invalidations below trigger
      // refetches whose queryFn reads that cache, and a stale hit would serve
      // the photo this mutation just replaced.
      invalidateMemberPhoto(PHOTO_BUCKET, variables.member_id);

      // Invalidate individual member photo queries
      queryClient.invalidateQueries({ queryKey: MEMBER_PHOTO_QUERY_KEYS.WEBCAM_BY_MEMBER(variables.member_id) });
      
      // Invalidate all member photo queries
      queryClient.invalidateQueries({ queryKey: MEMBER_PHOTO_QUERY_KEYS.ALL_WEBCAM_BY_MEMBER(variables.member_id) });
      
      // Invalidate bulk photo queries that might include this member
      queryClient.invalidateQueries({ 
        queryKey: MEMBER_PHOTO_QUERY_KEYS.BULK_WEBCAM_ALL,
        predicate: (query) => {
          // BULK_WEBCAM(ids) is BULK_WEBCAM_ALL followed by the id list.
          const memberIds = query.queryKey[MEMBER_PHOTO_QUERY_KEYS.BULK_WEBCAM_ALL.length];
          return Array.isArray(memberIds) && memberIds.includes(variables.member_id);
        }
      });
    },
  });
}

export function useDeleteMemberWebcamPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ photoId }: { photoId: string; memberId: string }) => {
      try {
        const { error } = await supabase
          .from('member_webcam_photos')
          .delete()
          .eq('id', photoId);

        if (error) {
          uploadError('Error deleting webcam photo', error, { photoId });
          throw new Error('Failed to delete photo');
        }
      } catch (err) {
        uploadError('Unexpected error in useDeleteMemberWebcamPhoto', err, { photoId });
        throw new Error(
          err instanceof Error 
            ? err.message 
            : 'Failed to delete photo. Please try again.'
        );
      }
    },
    onSuccess: (_, variables) => {
      // Drop the shared per-member entry FIRST — see the create mutation.
      invalidateMemberPhoto(PHOTO_BUCKET, variables.memberId);

      // Invalidate individual member photo queries
      queryClient.invalidateQueries({ queryKey: MEMBER_PHOTO_QUERY_KEYS.WEBCAM_BY_MEMBER(variables.memberId) });
      
      // Invalidate all member photo queries
      queryClient.invalidateQueries({ queryKey: MEMBER_PHOTO_QUERY_KEYS.ALL_WEBCAM_BY_MEMBER(variables.memberId) });
      
      // Invalidate bulk photo queries that might include this member
      queryClient.invalidateQueries({ 
        queryKey: MEMBER_PHOTO_QUERY_KEYS.BULK_WEBCAM_ALL,
        predicate: (query) => {
          // BULK_WEBCAM(ids) is BULK_WEBCAM_ALL followed by the id list.
          const memberIds = query.queryKey[MEMBER_PHOTO_QUERY_KEYS.BULK_WEBCAM_ALL.length];
          return Array.isArray(memberIds) && memberIds.includes(variables.memberId);
        }
      });
    },
  });
}

/**
 * Bulk hook for fetching multiple member photos at once.
 *
 * Two layers, and the distinction matters:
 *
 * - THIS query is keyed on the id array it was handed, so it still forks per
 *   distinct list (page 2 of the members table is a different key from page 1).
 *   That is fine, because it is now only a *projection*.
 * - `member-photo-cache` holds the actual answer PER MEMBER. A cold key whose
 *   members are all cached resolves with zero network requests, and a partly
 *   cached one asks only for the ids it is missing.
 *
 * Before that split, every new id array re-fetched photos the browser already
 * had — two round trips each (the RPC, then a Storage batch-sign) — which made
 * these two endpoints 45% of all production API traffic. See the header comment
 * in `src/lib/member-photo-cache.ts` for the measurements.
 *
 * Ids are normalized (invalid ones dropped, deduped, sorted) before they become
 * the cache key, so `[a, b]` and `[b, a]` are one entry rather than two.
 *
 * `keepPreviousData` is for callers whose id list changes as the user types
 * (the global header search): each new list is still its own key, so without it
 * already-visible thumbnails would blink back to the placeholder between
 * renders. Lookups are by member id, so serving the previous map can only ever
 * return the right member's photo or nothing.
 */
export function useBulkMemberPhotos(
  memberIds: string[],
  options?: { keepPreviousData?: boolean },
) {
  // Normalize once per caller-array identity (every list caller memoizes its
  // id array), then rebuild a referentially stable array from the signature.
  // Both halves matter: an unstable query key would defeat React Query's cache,
  // and re-normalizing on every render would put an O(n) pass in a hot path.
  const idsSignature = useMemo(() => normalizeMemberIds(memberIds).join(','), [memberIds]);
  const normalizedIds = useMemo(
    () => (idsSignature ? idsSignature.split(',') : []),
    [idsSignature],
  );

  // A complete cached answer lets a warm list render immediately instead of
  // flashing a skeleton. Only a COMPLETE one is used: a partial map would draw
  // initials avatars for members whose fetch is still in flight, which reads as
  // a wrong answer rather than a pending one.
  const completeCachedPhotos = useMemo(() => {
    if (!normalizedIds.length) return undefined;

    const { photos, missingIds } = readMemberPhotoCache<MemberPhoto>(PHOTO_BUCKET, normalizedIds);
    return missingIds.length ? undefined : photos;
  }, [normalizedIds]);

  const keepPrevious = options?.keepPreviousData ?? false;

  // Stable identity on purpose. React Query only reuses a previous placeholder
  // result when the `placeholderData` OPTION is referentially equal
  // (queryObserver.js: `options.placeholderData === prevResultOptions?.placeholderData`).
  // An inline arrow would fail that check every render, hand back a fresh
  // object each time, and re-fire the signed-URL refresh effect below on every
  // single render.
  const placeholderData = useCallback(
    (previousData: Record<string, MemberPhoto> | undefined) =>
      completeCachedPhotos ?? (keepPrevious ? previousData : undefined),
    [completeCachedPhotos, keepPrevious],
  );

  const query = useQuery({
    queryKey: MEMBER_PHOTO_QUERY_KEYS.BULK_WEBCAM(normalizedIds),
    placeholderData,
    queryFn: async () => {
      if (!normalizedIds.length) {
        return {};
      }

      const { photos: cachedPhotos, missingIds } = readMemberPhotoCache<MemberPhoto>(
        PHOTO_BUCKET,
        normalizedIds,
      );

      // Everything asked for is already held and still signed. This is the
      // path that removes the traffic — no RPC, no Storage round trip.
      if (!missingIds.length) {
        return cachedPhotos;
      }

      // Snapshot BEFORE the two awaits below. If a photo mutation invalidates a
      // member, or an identity change clears the cache, while this fetch is in
      // flight, the write at the end is dropped rather than resurrecting the
      // photo that was just replaced (or the previous admin's signed URLs).
      const cacheGeneration = getMemberPhotoCacheGeneration();

      try {
        // Fetch ONLY the latest photo per member, and only for the members we
        // are actually missing. The list renders a single thumbnail per member,
        // so signing a URL for every historical photo (as the old `.in()`
        // select did) was wasted work that eventually blew past Storage's
        // 1000-path createSignedUrls cap (HOK-OS-47). DISTINCT ON runs
        // server-side and the jsonb return sidesteps PostgREST's 1000-row cap.
        const { data, error } = await supabase.rpc('get_latest_webcam_photo_per_member', {
          p_member_ids: missingIds,
        });

        if (error) {
          // Transient network errors (e.g. Safari "Load failed") are
          // non-critical for thumbnail display — serve whatever the cache holds
          // instead of throwing, and cache nothing: a failure is not an answer.
          if (isTransientNetworkError(error)) {
            console.warn('[Database] Transient network error fetching bulk webcam photos, returning cached subset', {
              memberCount: normalizedIds.length,
              missingCount: missingIds.length,
              errorMessage: error.message,
            });
            return cachedPhotos;
          }

          dbError('Error fetching bulk webcam photos', error, {
            memberCount: normalizedIds.length,
            missingCount: missingIds.length,
            errorDetails: error
          });
          throw new Error('Failed to fetch member photos');
        }

        // The RPC returns a jsonb array of one latest row per member.
        const rows = (data ?? []) as Array<RawMemberWebcamPhoto & { member_id: string }>;

        // Sign only the sizes a list/grid renders — see LIST_PHOTO_VARIANTS.
        const photosWithSignedUrls = await addSignedUrlsToPhotos(
          rows,
          PHOTO_BUCKET,
          LIST_PHOTO_VARIANTS,
        );

        // One row per member already (DISTINCT ON), and `addSignedUrlsToPhotos`
        // maps 1:1 over its input, so index alignment holds.
        const fetchedPhotos: Record<string, MemberPhoto> = {};

        photosWithSignedUrls.forEach((photo, index) => {
          const memberId = rows[index].member_id;
          fetchedPhotos[memberId] = {
            id: photo.id,
            original_photo_url: photo.original_photo_url,
            large_photo_url: photo.large_photo_url,
            thumbnail_photo_url: photo.thumbnail_photo_url,
            uploaded_at: photo.uploaded_at,
            original_photo_path: photo.original_photo_path ?? null,
            large_photo_path: photo.large_photo_path ?? null,
            thumbnail_photo_path: photo.thumbnail_photo_path ?? null,
            original_photo_url_expires_at: photo.original_photo_url_expires_at ?? null,
            large_photo_url_expires_at: photo.large_photo_url_expires_at ?? null,
            thumbnail_photo_url_expires_at: photo.thumbnail_photo_url_expires_at ?? null,
          };
        });

        // Every id we asked about now has an answer — including the ones the
        // RPC returned no row for, which genuinely have no photo. Caching that
        // negative is what stops the photo-less members being re-queried by
        // every subsequent list. Guarded by the generation snapshot above.
        writeMemberPhotoCache(PHOTO_BUCKET, missingIds, fetchedPhotos, {
          generation: cacheGeneration,
        });

        return { ...cachedPhotos, ...fetchedPhotos };
      } catch (err) {
        // Transient network errors — degrade to the cached subset gracefully.
        if (isTransientNetworkError(err)) {
          console.warn('[Database] Transient network error in useBulkMemberPhotos, returning cached subset', {
            memberCount: normalizedIds.length,
            errorMessage: err instanceof Error ? err.message : String(err),
          });
          return cachedPhotos;
        }

        dbError('Unexpected error in useBulkMemberPhotos', err, { memberCount: normalizedIds.length });
        throw new Error('Failed to fetch member photos');
      }
    },
    // Matched to the per-member cache TTL so the two layers expire together.
    // Note this is no longer the knob that governs traffic: even when the query
    // is stale and refetches, the queryFn serves it from the per-member cache
    // without a request. It is the cache TTL that bounds staleness.
    staleTime: MEMBER_PHOTO_CACHE_TTL_MS,
    gcTime: 60 * 60 * 1000,
    enabled: memberIds.length > 0,
  });

  const photoList = useMemo(
    () => (query.data ? Object.values(query.data) : []),
    [query.data],
  );
  useSignedUrlRefresh(photoList, query.refetch);

  return query;
}

/**
 * Single-member convenience wrapper over the same cache the lists use.
 *
 * Exists for the surfaces that render ONE member and have no list to inherit a
 * photo from (a membership's member row, a confirmation modal). It is cheap for
 * the reason the bulk hook is: the member has almost always already been cached
 * by the list the admin navigated in from, so this resolves without a request.
 * Prefer passing `photo`/`isPhotoLoading` down from a bulk fetch in anything
 * that renders more than one member.
 */
export function useMemberPhoto(memberId: string | null | undefined) {
  const memberIds = useMemo(() => (memberId ? [memberId] : []), [memberId]);
  const query = useBulkMemberPhotos(memberIds);

  return {
    photo: memberId ? query.data?.[memberId] ?? null : null,
    isLoading: query.isLoading,
  };
}
